import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

interface UseNumberKeySelectionOptions {
    /** Only listen while the menu is interactive (active, not yet submitted). */
    enabled: boolean;
    /** Number of numbered options (keys 1..optionCount are handled). */
    optionCount: number;
    /** Called with the 0-based index of the option whose number was pressed. */
    onSelectIndex: (index: number) => void;
}

/**
 * Web-only: pressing a numbered option's key (1-9) selects it, mirroring a
 * click. No-op on native. Ignores presses while the member is typing in an
 * input/textarea/contenteditable, and any modifier combos.
 */
export const useNumberKeySelection = ({
    enabled,
    optionCount,
    onSelectIndex,
}: UseNumberKeySelectionOptions): void => {
    // Keep the latest callback without re-subscribing the listener each render.
    const onSelectIndexRef = useRef(onSelectIndex);
    onSelectIndexRef.current = onSelectIndex;

    useEffect(() => {
        if (Platform.OS !== 'web' || !enabled) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.metaKey || event.ctrlKey || event.altKey) {
                return;
            }

            const target = event.target as HTMLElement | null;
            const tagName = target?.tagName;
            if (
                tagName === 'INPUT' ||
                tagName === 'TEXTAREA' ||
                target?.isContentEditable === true
            ) {
                return;
            }

            const optionNumber = Number(event.key);
            if (
                !Number.isInteger(optionNumber) ||
                optionNumber < 1 ||
                optionNumber > optionCount
            ) {
                return;
            }

            event.preventDefault();
            onSelectIndexRef.current(optionNumber - 1);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [enabled, optionCount]);
};
